/*
 * image-hover-zoom.js
 * UrbanLadder-style hover magnifier for the PDP main image.
 *
 * Lens + side-panel pattern (like js-image-zoom): a lens box tracks the
 * cursor over the active main image, and a floating panel to the right
 * shows the corresponding high-res crop by panning a background-image.
 *
 * Desktop / fine-pointer only. Reuses the slide's `data-src` (1946w) as
 * the high-res source, so no extra image request beyond what the gallery
 * already knows about. Loaded only when the section setting is on.
 */
(function () {
  var mq = window.matchMedia('(min-width: 768px) and (hover: hover) and (pointer: fine)');
  if (!mq.matches) return;

  var ZOOM = 3; // ~3x, UrbanLadder-like
  var GAP = 16; // px between image column and panel

  function initGallery(gallery) {
    if (gallery.dataset.hoverZoomReady === 'true') return;
    gallery.dataset.hoverZoomReady = 'true';

    var wrapper = gallery.closest('.product__media-wrapper') || gallery.parentElement;
    if (!wrapper) return;
    if (getComputedStyle(wrapper).position === 'static') wrapper.style.position = 'relative';

    var panel = document.createElement('div');
    panel.className = 'pdp-zoom-panel';
    panel.setAttribute('aria-hidden', 'true');
    // The theme's theme.css hides every empty div (`div:empty { display:none }`,
    // specificity beats our `.is-visible` rule). Give the panel a child so it is
    // never `:empty` and our display toggle wins.
    panel.appendChild(document.createElement('span'));
    wrapper.appendChild(panel);

    var lens = document.createElement('div');
    lens.className = 'pdp-zoom-lens';
    lens.setAttribute('aria-hidden', 'true');

    var activeContainer = null;
    var activeImg = null;
    var rect = null; // displayed image rect (viewport coords)
    var inside = false; // is the cursor currently within the gallery?
    var lastX = 0, lastY = 0; // last known cursor position (viewport coords)

    function highResSrc(item) {
      return (item && item.dataset && item.dataset.src) ||
        (activeImg && (activeImg.currentSrc || activeImg.src));
    }

    function show(container) {
      var item = container.closest('.product__media-item');
      // Images only — skip video / model / 3D slides.
      if (item && item.dataset.mediaType && item.dataset.mediaType !== 'image') return false;
      // Skip slides hidden by the variant-media filter or off-screen duplicates.
      if (item && item.offsetParent === null) return false;

      var img = container.querySelector('img');
      if (!img) return false;

      rect = img.getBoundingClientRect();
      if (!rect.width || !rect.height) return false;

      activeContainer = container;
      activeImg = img;

      var wrapRect = wrapper.getBoundingClientRect();
      var avail = window.innerWidth - (wrapRect.left + wrapRect.width) - GAP - 16;
      var panelW = Math.max(280, Math.min(rect.width, avail));
      var panelH = rect.height;

      panel.style.width = panelW + 'px';
      panel.style.height = panelH + 'px';
      panel.style.left = (wrapRect.width + GAP) + 'px';
      panel.style.top = (rect.top - wrapRect.top) + 'px';
      panel.style.backgroundImage = 'url("' + highResSrc(item) + '")';
      panel.style.backgroundSize = (rect.width * ZOOM) + 'px ' + (rect.height * ZOOM) + 'px';
      panel.classList.add('is-visible');

      lens.style.width = (panelW / ZOOM) + 'px';
      lens.style.height = (panelH / ZOOM) + 'px';
      container.appendChild(lens);
      lens.style.display = 'block';
      return true;
    }

    function applyZoom(clientX, clientY) {
      if (!activeImg || !rect) return;
      var x = clientX - rect.left;
      var y = clientY - rect.top;

      var lensW = lens.offsetWidth;
      var lensH = lens.offsetHeight;

      // Lens centered on cursor, clamped to the image box.
      var lx = Math.max(0, Math.min(x - lensW / 2, rect.width - lensW));
      var ly = Math.max(0, Math.min(y - lensH / 2, rect.height - lensH));

      // Account for the image's offset inside its container (object-fit gaps).
      lens.style.left = (activeImg.offsetLeft + lx) + 'px';
      lens.style.top = (activeImg.offsetTop + ly) + 'px';

      panel.style.backgroundPosition = '-' + (lx * ZOOM) + 'px -' + (ly * ZOOM) + 'px';
    }

    function hide() {
      if (!activeContainer) return;
      panel.classList.remove('is-visible');
      panel.style.backgroundImage = '';
      lens.style.display = 'none';
      if (lens.parentElement) lens.parentElement.removeChild(lens);
      activeContainer = null;
      activeImg = null;
      rect = null;
    }

    // The image container currently under the given viewport point (if any).
    // The lens/panel are pointer-events:none, so they're transparent to this
    // hit-test and never mask the image below them.
    function containerAt(x, y) {
      var el = document.elementFromPoint(x, y);
      var c = el && el.closest ? el.closest('.product__media-item-container') : null;
      return c && gallery.contains(c) ? c : null;
    }

    // Re-evaluate what's actually under the cursor and update the zoom. This is
    // the key to switching: a new slide (thumbnail click, arrows, swipe) is
    // picked up here WITHOUT the user having to leave and re-hover — we no longer
    // trust a cached "active container", we re-detect it every time.
    function sync() {
      if (!inside) return;
      var container = containerAt(lastX, lastY);
      if (!container) { hide(); return; }
      if (container !== activeContainer) {
        if (!show(container)) { hide(); return; }
      }
      applyZoom(lastX, lastY);
    }

    var scheduled = false;
    function scheduleSync() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () { scheduled = false; sync(); });
    }

    gallery.addEventListener('pointerenter', function (e) {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      inside = true;
    });

    gallery.addEventListener('pointermove', function (e) {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      inside = true;
      lastX = e.clientX;
      lastY = e.clientY;
      sync();
    });

    gallery.addEventListener('pointerleave', function () {
      inside = false;
      hide();
    });

    // A slide change animates the swiper wrapper's transform. Re-sync once it
    // settles, so an image switched under a stationary cursor updates too.
    var sliderWrap = gallery.querySelector('.swiper-wrapper') || gallery;
    sliderWrap.addEventListener('transitionend', function (e) {
      if (e.propertyName === 'transform') scheduleSync();
    });

    // Fallback for instant (non-animated) active-slide changes.
    if (window.MutationObserver) {
      new MutationObserver(scheduleSync).observe(gallery, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });
    }

    // Geometry changed — re-evaluate against the last cursor position.
    window.addEventListener('scroll', scheduleSync, { passive: true });
    window.addEventListener('resize', scheduleSync);
  }

  function boot() {
    var galleries = document.querySelectorAll('media-gallery[data-enable-hover-zoom="true"]');
    for (var i = 0; i < galleries.length; i++) initGallery(galleries[i]);
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();